/**
 * WorkflowList
 * Level 0: shows all top-level workflow rows (sagas + standalone).
 * Handles the 3-level panel navigation state.
 *
 * Navigation state:
 *   { view: 'list' } — top-level list
 *   { view: 'chain'; sagaId: string } — saga member chain (level 1)
 *   { view: 'member'; sagaId: string; memberId: string | null; memberIndex: number } — member detail (level 2)
 */

import { useState, useEffect } from 'react';
import { SagaRow } from './SagaRow';
import { AtomicRow } from './AtomicRow';
import { MemberChainView } from './MemberChainView';
import { MemberDetailView } from './MemberDetailView';
import { Skeleton } from '../ui/skeleton';
import { useWorkflowPanel } from './useWorkflowPanel';
import type { MemberWorkflowSummary } from '../../types/workflow';

type PanelView =
  | { view: 'list' }
  | { view: 'chain'; sagaId: string }
  | { view: 'member'; sagaId: string; memberIndex: number };

// NOTE: the drill-in view is in-memory only. The desktop rail does not unmount or collapse on
// navigation (it is an always-mounted side panel), so the user's place is preserved across the
// common navigate-from-a-CTA flow without any storage. We intentionally do NOT persist the view
// to sessionStorage: restoring a deep view on a fresh load races the workflow data fetch (the
// parent saga isn't loaded yet, so the correction effect bounces back to the list anyway) and is
// unnecessary now that the panel stays open.
export function WorkflowList() {
  const { workflows, isLoading, isError, isFetching, refetch } = useWorkflowPanel();
  const [panelView, setPanelView] = useState<PanelView>({ view: 'list' });

  const activeWorkflows = workflows.filter((w) => w.status !== 'dismissed');
  // Only surface the error screen when there is NOTHING else to show. react-query keeps the last
  // successful list on a background-refetch failure, so when `workflows` is still populated we keep
  // rendering it (stale-but-valid) instead of replacing a good list with an error.
  const showError = isError && activeWorkflows.length === 0;

  // When the active saga/member is no longer found (e.g. dismissed while viewing),
  // fall back to the appropriate parent view. Done in useEffect to avoid
  // calling setState during render (React warning).
  useEffect(() => {
    if (panelView.view === 'chain') {
      const saga = workflows.find((w) => w.id === panelView.sagaId);
      if (!saga) {
        setPanelView({ view: 'list' });
      }
    } else if (panelView.view === 'member') {
      const parent = workflows.find((w) => w.id === panelView.sagaId);
      if (!parent || !parent.members[panelView.memberIndex]) {
        // Saga members fall back to the chain; standalone atomics fall back to the list.
        setPanelView(
          parent && parent.kind === 'saga'
            ? { view: 'chain', sagaId: parent.id }
            : { view: 'list' },
        );
      }
    }
  }, [workflows, panelView]);

  if (panelView.view === 'chain') {
    const wf = workflows.find((w) => w.id === panelView.sagaId);
    const saga = wf && wf.kind === 'saga' ? wf : undefined;
    if (saga) {
      return (
        <MemberChainView
          saga={saga}
          onBack={() => setPanelView({ view: 'list' })}
          onSelectMember={(member: MemberWorkflowSummary) => {
            const idx = saga.members.indexOf(member);
            setPanelView({ view: 'member', sagaId: saga.id, memberIndex: idx >= 0 ? idx : 0 });
          }}
        />
      );
    }
    // Saga not yet removed from state — render nothing while useEffect corrects it
    return null;
  }

  if (panelView.view === 'member') {
    const parent = workflows.find((w) => w.id === panelView.sagaId);
    if (parent) {
      const member = parent.members[panelView.memberIndex];
      if (member) {
        return (
          <MemberDetailView
            member={member}
            backLabel={parent.kind === 'saga' ? 'Back to member chain' : 'Back to list'}
            onBack={() =>
              parent.kind === 'saga'
                ? setPanelView({ view: 'chain', sagaId: parent.id })
                : setPanelView({ view: 'list' })
            }
          />
        );
      }
    }
    // Not yet corrected by useEffect — render nothing while it catches up
    return null;
  }

  // Level 0: list view
  return (
    <div data-testid="workflow-list" className="flex flex-col gap-2 p-4" role="list" aria-label="Active workflows">
      {/* No h2 here — the Rail/Sheet that mounts WorkflowList already provides the heading */}
      <div className="flex items-center justify-end mb-1">
        {activeWorkflows.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {activeWorkflows.length} active
          </span>
        )}
      </div>

      {isLoading && (
        <>
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </>
      )}

      {/* Error state — only when there's nothing else to show (see showError). Distinct from
          "All caught up" so a failed fetch doesn't masquerade as an empty list. Offers a retry,
          which reflects its in-flight state instead of looking inert. */}
      {!isLoading && showError && (
        <div
          data-testid="workflow-list-error"
          role="alert"
          className="flex flex-col items-center justify-center py-8 text-center"
        >
          <div
            className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3"
            aria-hidden="true"
          >
            <svg className="w-5 h-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-foreground">Couldn’t load workflows</p>
          <p className="text-xs text-muted-foreground mt-0.5 mb-3">Something went wrong fetching your workflows.</p>
          <button
            type="button"
            data-testid="workflow-list-retry"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-busy={isFetching}
            className="px-4 min-h-[44px] text-sm font-medium text-primary border border-primary/40 rounded-md hover:bg-primary/5 disabled:opacity-60 disabled:cursor-default"
          >
            {isFetching ? 'Retrying…' : 'Try again'}
          </button>
        </div>
      )}

      {!isLoading && !showError && activeWorkflows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div
            className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3"
            aria-hidden="true"
          >
            <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-foreground">All caught up</p>
          <p className="text-xs text-muted-foreground mt-0.5">No active workflows</p>
        </div>
      )}

      {/* Render rows whenever there is data — even if isError (a background refetch failed): keep
          showing the last-good list rather than blanking it. */}
      {!isLoading && activeWorkflows.length > 0 &&
        activeWorkflows.map((workflow) =>
          workflow.kind === 'saga' ? (
            <SagaRow
              key={workflow.id}
              saga={workflow}
              onDrillIn={(saga) => setPanelView({ view: 'chain', sagaId: saga.id })}
            />
          ) : (
            <AtomicRow
              key={workflow.id}
              workflow={workflow}
              onDrillIn={(wf) => setPanelView({ view: 'member', sagaId: wf.id, memberIndex: 0 })}
            />
          ),
        )}
    </div>
  );
}
